import { HttpLink } from 'apollo-angular/http';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerFeConfig } from 'src/app/modules/core/interfaces/environment.interface';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import { LogoutAction } from '../enums/logout-action.enum';
import { NotifyOrConfirmType } from '../enums/notify-or-confirm-type.enum';
import { UserState } from '../enums/user-state.enum';
import { Session } from '../models/session.model';
import { User } from '../models/user.model';
import { AuthSchema } from '../schemas/auth.schema';
import { GraphqlService } from './graphql.service';
import { LoaderService } from './loader.service';
import { LoggerService } from './logger.service';
import { NotifyOrConfirmService } from './notify-or-confirm.service';
import { SessionService } from './session.service';
import { UserService } from './user.service';
import { WindowRefService } from './window.service';
import { CookieService } from 'ngx-cookie-service';

/**
 * Authentication Service
 */
@Injectable({
	providedIn: 'root'
})
export class AuthService extends GraphqlService {
	private loadedConfig: CustomerFeConfig;

	// =======================================================================================================================================
	// Constructor
	// =======================================================================================================================================

	/**
	 * Constructor
	 */
	constructor(
		protected sessionService: SessionService,
		protected configService: ConfigService,
		protected cookieService: CookieService,
		protected notifyOrConfirmService: NotifyOrConfirmService,
		protected translateService: TranslateService,
		protected userService: UserService,
		protected windowRef: WindowRefService,
		protected router: Router,
		protected http: HttpClient,
		protected httpLink: HttpLink,
		protected logger: LoggerService,
		protected loaderService: LoaderService,
		protected toastrService: ToastrService,
		protected ngZone: NgZone
	) {
		super(
			http,
			httpLink,
			configService,
			loaderService,
			notifyOrConfirmService,
			sessionService,
			translateService,
			router,
			logger,
			ngZone,
			toastrService,
			windowRef
		);
		this.configService.configObservable.subscribe((config) => {
			this.loadedConfig = config;
		});
	}

	// =======================================================================================================================================
	// Methods
	// =======================================================================================================================================

	/**
	 * Login via idToken & code from IdentityServer
	 *
	 * @param idToken (from login via authentication server)
	 * @param code (from login via authentication server)
	 * @param redirect
	 * @returns
	 */
	public login(idToken: string, code: string, redirect: boolean = false): Observable<{ user: User }> {
		this.sessionService.idToken = idToken;
		return new Observable((subscriber) => {
			// Get session from authentication server
			this.http
				.post(this.loadedConfig.sessionCreationUri + this.loadedConfig.clientId, {
					Code: code,
					IdToken: idToken,
					ApplicationVersion: '1.0.0',
					MacAddress: '00:00:00:00:00:00'
				})
				.subscribe(
					(session) => {
						// Process session
						this.processSession(session, redirect, true).subscribe(
							(data) => {
								subscriber.next(data);
								subscriber.complete();
							},
							(err) => {
								subscriber.error(err);
							}
						);
					},
					(err) => {
						this.processError(err, subscriber, redirect);
					}
				);
		});
	}

	/**
	 * Login via session (e.g. from PortalClient)
	 */
	public loginViaSessionId(sessionId: string, redirect: boolean = false): Observable<{ user: User }> {
		// Remove old session data
		this.sessionService.clear(false);

		return new Observable((subscriber) => {
			// Get session from authentication server
			this.http.get(this.loadedConfig.sessionLoginUri + this.loadedConfig.clientId + '/' + sessionId).subscribe(
				(response) => {
					// Process session
					this.processSession(response, redirect, false).subscribe(
						(data) => {
							subscriber.next(data);
							subscriber.complete();
						},
						(err) => {
							subscriber.error(err);
						}
					);
				},
				(err) => {
					this.processError(err, subscriber, redirect);
				}
			);
		});
	}

	/**
	 * Login via idToken & code from IdentityServer
	 *
	 * @param session
	 * @param redirect
	 * @param setLogoutUri Logout should be set when user logged in via IndetityServer (and not by AuthenticationServer session)
	 * @returns
	 */
	public processSession(session: object, redirect: boolean = false, setLogoutUri: boolean = true): Observable<{ user: User }> {
		return new Observable((subscriber) => {
			// Set session
			this.sessionService.session = Session.map(session);

			// Get user uid
			const userUid: string = _.get(this.sessionService.session, 'user.uid');
			if (!userUid) {
				if (redirect) {
					this.router.navigate(['/user-auth/logout']);
				}
				subscriber.error('Missing user.uid in session');
				return;
			}

			// Get user
			this.userService
				.getUserWithCredit(userUid)
				.pipe(
					map((userWithCredit: { user: User }) => {
						// Check data
						if (!userWithCredit || !userWithCredit.user) {
							return null;
						}

						// redirect user to start page and remove session
						if (userWithCredit.user.userState === UserState.DISABLED) {
							this.notifyOrConfirmService.showNotification(
								this.translateService.instant('authService.userDeactivatedTitle'),
								this.translateService.instant('authService.userDeactivatedMessage'),
								NotifyOrConfirmType.ERROR,
								this.translateService.instant('authService.buttonText')
							);
							this.router.navigate(['/user-auth/logout']);
							subscriber.error('Disabled user');
							return;
						}

						// Set session user
						this.sessionService.currentUser = userWithCredit.user;

						// Set logout URL
						if (setLogoutUri) {
							this.sessionService.logoutUri =
								this.loadedConfig.logoutUri +
								this.loadedConfig.clientId +
								'/' +
								this.sessionService.session.uid +
								'/' +
								userWithCredit.user.externalUid +
								'?id_token=' +
								this.sessionService.idToken;
						}

						// Set current user
						return {
							user: userWithCredit.user
						};
					})
				)
				.subscribe(
					(userWithCredit: { user: User }) => {
						if (userWithCredit) {
							subscriber.next(userWithCredit);
							subscriber.complete();
						} else {
							if (redirect) {
								this.router.navigate(['/user-auth/logout']);
							}
							subscriber.error('Unable to get user');
						}
					},
					(err) => {
						if (redirect) {
							this.router.navigate(['/user-auth/logout']);
						}
						subscriber.error(err);
					}
				);
		});
	}

	/**
	 * Login with a registrator user
	 */
	public loginRegistratorUser(): Observable<boolean> {
		return new Observable((subscriber) => {
			// Get session from authentication server
			this.http
				.post(this.loadedConfig.sessionCreationUri + this.loadedConfig.clientId + '/registrator', {
					ApplicationVersion: '1.0.0',
					DongleId: 'CustomerFrontend',
					MacAddress: '00:00:00:00:00:00'
				})
				.subscribe(
					(session) => {
						// Set session without user
						this.sessionService.session = Session.map(session);
						this.sessionService.currentUser = null;

						subscriber.next(true);
						subscriber.complete();
					},
					(err) => {
						this.logger.error(err);
						subscriber.error(err);
					}
				);
		});
	}

	/**
	 * Update current user
	 *
	 * @param userId
	 * @param data
	 * @param avatarGuid
	 * @param deleteAvatar
	 * @returns
	 */
	public updateCurrentUser(userId: string, data: any, avatarGuid: string = null, deleteAvatar: boolean = null): Observable<User> {
		return this.userService.update(userId, data, null, { deleteAvatar, avatarGuid }).pipe(
			map((user: User) => {
				this.sessionService.currentUser = user;
				return this.sessionService.currentUser;
			})
		);
	}

	/**
	 * Logout
	 */
	public logout(performRedirect: boolean = true, logoutAction: LogoutAction = LogoutAction.LOGOUT_TO_START_PAGE): Observable<void> {
		return new Observable((subscriber) => {
			// Create logout URI
			const uri = this.sessionService.logoutUri;

			// Redirect
			const clearAndRedirect = (error: any = null) => {
				// Clear (must be done after endSession, otherwise session.uid is missing for the request)
				this.sessionService.clear(true);

				// Perform redirect
				if (performRedirect) {
					if (uri) {
						this.loaderService.loader.startLoading();
						if (logoutAction === LogoutAction.LOGOUT_TO_LOGIN_PAGE) {
							this.cookieService.set('redirectToLogin', 'true', 0, '/');
						}
						this.windowRef.nativeWindow.open(uri, '_self');
					} else {
						switch (logoutAction) {
							case LogoutAction.LOGOUT_TO_START_PAGE:
								this.router.navigate(['/']);
								break;
							case LogoutAction.LOGOUT_TO_LOGIN_PAGE:
								this.windowRef.nativeWindow.open(this.loadedConfig.loginUri + 'customerFe', '_self');
								break;

							default:
								this.router.navigate(['/']);
								break;
						}
					}
				}

				if (error) {
					subscriber.error(error);
				} else {
					subscriber.next();
					subscriber.complete();
				}
			};

			// End session
			try {
				this.endSession().subscribe(
					() => {
						clearAndRedirect();
					},
					(error) => {
						this.logger.error(error);
						clearAndRedirect(error);
					}
				);
			} catch (e) {
				this.logger.error(e);
				clearAndRedirect(e);
			}
		});
	}

	/**
	 * Checks if sessionId belongs to user
	 */
	public isUsersSession(sessionId: string): Observable<boolean> {
		return this.query(AuthSchema.isUsersSession, { sessionId }).pipe(map((data: any) => (data ? data.isUsersSession : false)));
	}

	/**
	 * Logout session only
	 */
	public endSession(): Observable<boolean> {
		return new Observable((subscriber) => {
			// Check session
			if (!this.sessionService.session) {
				subscriber.next(false);
				subscriber.complete();
				return;
			}

			// Get session from authentication server
			this.http
				.get(this.loadedConfig.sessionEndUri + this.sessionService.session.uid, {
					headers: {
						Authorization: 'Bearer ' + this.sessionService.session.accessToken
					}
				})
				.subscribe(
					() => {
						subscriber.next(true);
						subscriber.complete();
					},
					(err) => {
						this.logger.error(err);
						subscriber.error(err);
					}
				);
		});
	}

	/**
	 * Check whether user is logged in or not and session is not expired
	 *
	 * @param autoRefresh try to refresh session if
	 * @param redirect
	 * @return
	 */
	loggedIn(autoRefresh: boolean = true, redirect: boolean = false): Observable<boolean> {
		return new Observable<boolean>((subscriber) => {
			// Check session
			this.sessionService.check(autoRefresh, redirect).subscribe((valid) => {
				// Invalid session
				if (!valid) {
					subscriber.next(false);
					subscriber.complete();
				} else if (!this.sessionService.currentUser) {
					// Missing current user
					if (redirect) {
						this.router.navigate(['/user-auth/logout/' + LogoutAction.SECURITY]);
					}
					subscriber.next(false);
					subscriber.complete();
				} else {
					// Session ok
					subscriber.next(true);
					subscriber.complete();
				}
			});
		});
	}

	/**
	 * Process error
	 */
	protected processError(err: any, subscriber: Subscriber<{ user: User }>, redirect: boolean = false) {
		this.logger.error(err);
		if (err && err.networkError && err.networkError.status === 401) {
			if (_.get(err, 'networkError.error.additionalInformation') === 'InvalidUserState') {
				this.notifyOrConfirmService.showNotification(
					this.translateService.instant('authService.userDeactivatedTitle'),
					this.translateService.instant('authService.userDeactivatedMessage'),
					NotifyOrConfirmType.ERROR,
					this.translateService.instant('authService.buttonText')
				);
				this.router.navigate(['/user-auth/logout']);
			}
		}
		if (redirect) {
			this.router.navigate(['/user-auth/logout']);
		}
		subscriber.error(err);
	}
}
